<template>
  <div class="music-player">
    <div class="music-btn" @click="toggleMusic">
      <img
        :src="isMusicPlaying ? start : pause"
        alt="音乐控制"
        class="music-icon"
        :class="{ rotating: isMusicPlaying }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import start from "@/assets/images/start.png";
import pause from "@/assets/images/pause.png";
import bgMusic from "@/assets/images/bg.mp3";

const isMusicPlaying = ref(false);
const isAudioLoaded = ref(false);
let audio: HTMLAudioElement | null = null;
let hasAttemptedAutoPlay = false;

const initAudio = () => {
  audio = new Audio();
  audio.src = bgMusic;
  audio.loop = true;
  audio.volume = 0.3;
  
  audio.addEventListener('canplaythrough', () => {
    console.log('音频加载完成，可以播放');
    isAudioLoaded.value = true;
  });
  
  audio.addEventListener('loadedmetadata', () => {
    console.log('音频元数据加载完成');
  });
  
  audio.addEventListener('error', (e) => {
    console.error('音频加载失败:', e);
    console.error('错误代码:', (e as ErrorEvent).target?.error?.code);
  });
  
  audio.addEventListener('stalled', () => {
    console.log('音频加载停滞');
  });
  
  // 开始加载
  audio.load();
};

const playMusic = async () => {
  if (!audio) {
    initAudio();
  }
  
  if (audio) {
    try {
      console.log('尝试播放音乐，音频状态:', audio.readyState);
      console.log('音频时长:', audio.duration);
      
      // 如果音频还没加载完成，等待一下
      if (audio.readyState < 2) {
        console.log('音频尚未加载完成，等待...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      const result = await audio.play();
      console.log('play() 返回结果:', result);
      isMusicPlaying.value = true;
      console.log('音乐播放成功');
    } catch (e: any) {
      console.error('音乐播放失败:', e);
      console.error('错误名称:', e.name);
      console.error('错误消息:', e.message);
      
      // 尝试使用 Web Audio API 绕过限制（仅限静音情况）
      tryWebAudioFallback();
    }
  }
};

const tryWebAudioFallback = () => {
  console.log('尝试 Web Audio API 回退方案');
  // 这个方案需要用户交互，作为最后的尝试
};

const pauseMusic = () => {
  if (audio) {
    audio.pause();
    isMusicPlaying.value = false;
    console.log('音乐已暂停');
  }
};

const toggleMusic = async () => {
  console.log('切换音乐状态, 当前状态:', isMusicPlaying.value);
  
  if (isMusicPlaying.value) {
    pauseMusic();
  } else {
    await playMusic();
  }
  
  console.log('切换后状态:', isMusicPlaying.value);
};

const isWeChat = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.includes('micromessenger');
};

const tryAutoPlayInWeChat = () => {
  if (hasAttemptedAutoPlay) return;
  hasAttemptedAutoPlay = true;
  
  if (isWeChat()) {
    console.log('检测到微信环境，尝试通过微信事件触发播放');
    
    // 方法1：监听 WeixinJSBridgeReady 事件
    const handleWeixinReady = () => {
      console.log('WeixinJSBridgeReady 触发，尝试自动播放');
      playMusic();
    };
    
    // 如果 WeixinJSBridge 已经存在，直接调用
    if (window.WeixinJSBridge) {
        console.log('WeixinJSBridge 已存在，直接调用');
      handleWeixinReady();
    } else {
      // 否则监听 ready 事件
      document.addEventListener('WeixinJSBridgeReady', handleWeixinReady, false);
    }
    
    // 方法2：监听 document 的 touchstart 事件（用户首次触摸）
    const handleFirstTouch = () => {
      console.log('用户首次触摸，尝试自动播放');
      playMusic();
      document.removeEventListener('touchstart', handleFirstTouch, false);
    };
    document.addEventListener('touchstart', handleFirstTouch, false);
  } else {
    // 非微信环境，尝试直接自动播放
    console.log('非微信环境，尝试自动播放');
    playMusic();
  }
};

onMounted(() => {
  initAudio();
  tryAutoPlayInWeChat();
});

onUnmounted(() => {
  if (audio) {
    audio.pause();
    audio = null;
  }
});
</script>

<style lang="scss" scoped>
.music-player {
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 9999;

  .music-btn {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:active {
      transform: scale(0.9);
    }
    
    .music-icon {
      width: 100%;
      height: 100%;
      object-fit: contain;
      transition: transform 0.3s ease;
      
      &.rotating {
        animation: rotate 2s linear infinite;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
